/**
 * 前端配置管理工具的核心实现模块.
 *
 * 提供从多种来源（环境变量、URL参数、全局变量）读取配置的能力，
 * 并支持配置项的依赖关系和值转换。
 *
 * @packageDocumentation
 * @author yusangeng@outlook.com
 */
import { __assign } from "tslib";
import Reader from "./Reader";
import helper from "./helper";
var DEFAULT_GLOBAL_NAME = "__Konph";
/**
 * 获取当前URL的查询字符串
 *
 * @returns 当前URL的查询字符串
 * @private
 */
function getSearch() {
    try {
        return globalThis.location.search;
    }
    catch (err) {
        // TODO: 兼容node命令行参数
        return "";
    }
}
/**
 * 读取配置数据.
 *
 * @param {KonphOptions<T>} options 读取设置, 键为感兴趣的配置字段名(不区分大小写, 两边的空格会被trim掉),
 *   值为相应的读取配置, 支持fit, deps, defaultValue, 具体见README.
 * @param {string|KonphInitData<T>} name 全局配置变量名, 默认为`__Konph`,
 *   如果传入一个对象, 则不读取全局变量和url参数,
 *   而是直接读传入对象的url和global字段, 用来在非浏览器环境中测试.
 * @returns {KonphResult<T>} 配置读取结果, 格式为键值对.
 * @function
 */
function getKonph(options, name) {
    var _a, _b, _c;
    var globalConf;
    var url;
    var g = globalThis;
    if (!name || (typeof name === "string" && !g[name])) {
        name = DEFAULT_GLOBAL_NAME;
    }
    if (typeof name === "string") {
        globalConf = (_a = g[name]) !== null && _a !== void 0 ? _a : {};
        url = getSearch();
    }
    else {
        globalConf = (_b = name.global) !== null && _b !== void 0 ? _b : {};
        url = (_c = name.url) !== null && _c !== void 0 ? _c : "";
    }
    // 环境变量会被全局变量覆盖
    if (typeof process !== "undefined") {
        var _d = process.env, env = _d === void 0 ? {} : _d;
        globalConf = __assign(__assign({}, env), globalConf);
    }
    var reader = new Reader(globalConf, url, options);
    var ret = Object.keys(options)
        .map(function (key) { return key.trim().toLowerCase(); })
        .map(function (key) {
        return { key: key, value: reader.item(key) };
    })
        .reduce(function (prev, el) {
        // fixme
        prev[el.key] = el.value;
        return prev;
    }, {});
    return ret;
}
/**
 * 创建私有配置项
 *
 * @param value - 配置项的值
 * @returns 私有配置项对象
 * @private
 */
var privateItem = function (value) {
    return {
        __konph_private_item__: true,
        value: value
    };
};
/**
 * 配置管理器的工厂函数
 *
 * @remarks
 * 该函数是getKonph的增强版本，附加了helper工具和private方法
 *
 * @example
 * // 使用private方法创建私有配置
 * const config = konph({
 *   secret: konph.private({
 *     default: 'my-secret',
 *     env: 'APP_SECRET'
 *   })
 * });
 */
var konph = (function () {
    var k = getKonph;
    k.helper = helper;
    k.private = privateItem;
    return k;
})();
export default konph;
//# sourceMappingURL=data:application/json;base64,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